import type { TextComposerAuthoringState } from "../editor/authoringState";
import type {
  TextComposerExtension,
  TransformContext,
} from "../editor/extensionContract";
import { composeTransforms } from "../editor/extensionRegistry";
import { defaultExtensions } from "../extensions/defaultExtensions";
import { TEXT_COMPOSER_DEFAULT_PRODUCT_MODEL } from "../extensions/mergeTags/mergeTagDefinitions";
import type { ProductGridModelValue } from "@/features/EmailEditor/plugins/productGridConstants";

type ChannelType = "TEXT";

export interface TextComposerRcsButton {
  type: "OPEN_URL";
  text: string;
  url: string;
}

export type TextComposerRcsSuggestion =
  | {
      type: "REPLY";
      text: string;
      postbackData: string;
    }
  | {
      type: "OPEN_URL";
      text: string;
      postbackData: string;
      url: string;
      application: "BROWSER";
    }
  | {
      type: "DIAL_PHONE";
      text: string;
      postbackData: string;
      /** E.164, separators stripped. */
      phoneNumber: string;
    };

interface TextComposerTextBody {
  type: ChannelType;
  text: string;
}

interface TextComposerMediaBody {
  // IMAGE for images, DOCUMENT for non-image attachments (vCards).
  type: "IMAGE" | "DOCUMENT";
  url: string;
  text?: string;
}

export interface TextComposerRcsPayload {
  content: {
    body: TextComposerTextBody | TextComposerMediaBody;
    buttons?: TextComposerRcsButton[];
    suggestions?: TextComposerRcsSuggestion[];
  };
}

export interface TextComposerSmsPayload {
  content: {
    body: TextComposerTextBody;
  };
}

export interface TextComposerMmsPayload {
  content: {
    body: TextComposerTextBody | TextComposerMediaBody;
  };
}

export interface TextComposerChannelTransforms {
  rcs: TextComposerRcsPayload | null;
  sms: TextComposerSmsPayload | null;
  mms: TextComposerMmsPayload | null;
}

export function buildChannelTransforms(
  state: TextComposerAuthoringState,
  selectedProductModel?: ProductGridModelValue,
  extensions?: TextComposerExtension[],
): TextComposerChannelTransforms {
  const productModel =
    selectedProductModel ?? TEXT_COMPOSER_DEFAULT_PRODUCT_MODEL;
  const exts = extensions ?? defaultExtensions;
  const ctx: TransformContext = { productModel };
  const transforms = composeTransforms(exts);

  const bodyText = (state.bodyText ?? "").trim();
  const hasMedia = (state.media?.length ?? 0) > 0;
  const hasCta =
    (state.ctaButton?.buttonText?.trim().length ?? 0) > 0 ||
    (state.ctaButton?.buttonUrl?.trim().length ?? 0) > 0;
  const hasContent = bodyText.length > 0 || hasMedia || hasCta;

  if (!hasContent) {
    return { rcs: null, sms: null, mms: null };
  }

  const baseSms: TextComposerSmsPayload = {
    content: { body: { type: "TEXT", text: bodyText } },
  };
  const baseRcs: TextComposerRcsPayload = {
    content: { body: { type: "TEXT", text: bodyText } },
  };
  const baseMms: TextComposerMmsPayload = {
    content: { body: { type: "TEXT", text: bodyText } },
  };

  const smsResult = transforms.transformSms(state, baseSms, ctx);
  const rcsResult = transforms.transformRcs(state, baseRcs, ctx);
  const mmsResult = transforms.transformMms(state, baseMms, ctx);

  const smsIsEmpty =
    smsResult.content.body.type === "TEXT" &&
    smsResult.content.body.text.length === 0;

  const rcsBodyEmpty =
    rcsResult.content.body.type === "TEXT"
      ? rcsResult.content.body.text.length === 0
      : false;
  const rcsHasButtons = (rcsResult.content.buttons?.length ?? 0) > 0;
  const rcsHasImage = rcsResult.content.body.type === "IMAGE";
  const rcsIsEmpty = rcsBodyEmpty && !rcsHasButtons && !rcsHasImage;

  const mmsBodyEmpty =
    mmsResult.content.body.type === "TEXT"
      ? mmsResult.content.body.text.length === 0
      : false;
  const mmsHasImage = mmsResult.content.body.type === "IMAGE";
  const mmsIsEmpty = mmsBodyEmpty && !mmsHasImage;

  return {
    sms: smsIsEmpty ? null : smsResult,
    rcs: rcsIsEmpty ? null : rcsResult,
    mms: mmsIsEmpty ? null : mmsResult,
  };
}
